import { useEffect, useRef, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import {
  downloadAll,
  removePdf,
  storageEstimate,
  usedBytes,
  type DownloadProgress,
} from '@/features/offline/storage'
import { baseUrl } from '@/services/catalog'
import {
  holePushZustand,
  schaltePushAus,
  schaltePushEin,
  type PushZustand,
} from '@/features/updates/push'
import { APP_VERSION } from '@/features/changelog/fassungen'
import { THEME_LABELS, useApp, type Theme } from '@/store/app'
import { ViewHeader } from '@/components/AppShell'
import { AenderungenDialog } from '@/components/AenderungenDialog'
import { Button, Label } from '@/components/primitives'

/**
 * Einstellungen und Offline-Verwaltung.
 *
 * Der wichtigste Knopf der App steht hier: „Alle Karten offline verfügbar machen".
 * Wer sich vor der Schicht darum kümmert, hat im Funkloch alles dabei.
 */
export function Settings() {
  const catalog = useApp((s) => s.catalog)
  const source = useApp((s) => s.source)
  const offlineStates = useApp((s) => s.offlineStates)
  const refreshOfflineStates = useApp((s) => s.refreshOfflineStates)
  const theme = useApp((s) => s.theme)
  const setTheme = useApp((s) => s.setTheme)

  const [progress, setProgress] = useState<DownloadProgress | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [verlaufOffen, setVerlaufOffen] = useState(false)
  const abortRef = useRef<AbortController | null>(null)

  // Reaktiv aus der Datenbank statt über einen Effekt: Die Anzeige zieht nach jedem
  // Download und jedem Löschen von selbst nach, ohne dass eine Stelle vergessen werden
  // kann, sie aufzufrischen.
  const storage = useLiveQuery(
    async () => ({ used: await usedBytes(), quota: await storageEstimate() }),
    [],
  )
  const used = storage?.used ?? 0
  const quota = storage?.quota ?? null

  if (!catalog) return null

  const missing = catalog.cards.filter((c) => offlineStates.get(c.id) === 'fehlt')
  const outdated = catalog.cards.filter((c) => offlineStates.get(c.id) === 'veraltet')
  const pending = [...missing, ...outdated]
  const pendingBytes = pending.reduce((sum, card) => sum + card.pdfBytes, 0)

  const startDownload = async () => {
    const controller = new AbortController()
    abortRef.current = controller
    setMessage(null)
    setProgress({ done: 0, total: pending.length, current: '' })

    const result = await downloadAll(catalog.cards, baseUrl, setProgress, controller.signal)

    setProgress(null)
    abortRef.current = null
    await refreshOfflineStates()

    if (result.failed.length > 0) {
      setMessage(
        `${result.loaded} Karten geladen, ${result.failed.length} fehlgeschlagen: ` +
          result.failed.map((f) => f.title).join(', '),
      )
    } else if (result.loaded > 0) {
      setMessage(`${result.loaded} Karten sind jetzt offline verfügbar.`)
    }
  }

  const removeAll = async () => {
    for (const card of catalog.cards) await removePdf(card.id)
    await refreshOfflineStates()
    setMessage('Alle heruntergeladenen Karten wurden entfernt.')
  }

  return (
    <div>
      <ViewHeader title="Einstellungen" />

      <section className="px-4 py-5">
        <Label>Offline-Verfügbarkeit</Label>

        <div className="mt-3 overflow-hidden rounded-card border border-line bg-surface">
          <div className="px-4 py-3">
            <p className="text-sm text-muted">
              {pending.length === 0 ? (
                <>
                  Alle <strong className="font-semibold text-ink">{catalog.cards.length} Karten</strong>{' '}
                  liegen auf dem Gerät und sind ohne Netzverbindung verfügbar.
                </>
              ) : (
                <>
                  <strong className="font-semibold text-ink">{pending.length} Karten</strong>{' '}
                  {outdated.length > 0 && missing.length > 0
                    ? `(${missing.length} neu, ${outdated.length} aktualisiert) `
                    : outdated.length > 0
                      ? '(aktualisiert) '
                      : ''}
                  fehlen noch — zusammen {formatBytes(pendingBytes)}.
                </>
              )}
            </p>

            {progress ? (
              <div className="mt-3">
                <div className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="truncate text-ink">{progress.current || 'Wird abgeschlossen …'}</span>
                  <span className="shrink-0 tabular-nums text-muted">
                    {progress.done} / {progress.total}
                  </span>
                </div>
                <div
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={progress.total}
                  aria-valuenow={progress.done}
                  className="mt-2 h-1.5 w-full overflow-hidden rounded-chip bg-sunken"
                >
                  <div
                    className="h-full bg-accent transition-[width] duration-200"
                    style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }}
                  />
                </div>
                <div className="mt-3">
                  <Button onClick={() => abortRef.current?.abort()} full>
                    Abbrechen
                  </Button>
                </div>
              </div>
            ) : (
              pending.length > 0 && (
                <div className="mt-3">
                  <Button variant="primary" onClick={() => void startDownload()} full>
                    {pending.length} Karten laden ({formatBytes(pendingBytes)})
                  </Button>
                </div>
              )
            )}
          </div>

          <div className="border-t border-line px-4 py-3 text-sm text-muted">
            <div className="flex justify-between gap-3">
              <span>Belegt durch Karten</span>
              <span className="tabular-nums text-ink">{formatBytes(used)}</span>
            </div>
            {quota && quota.quota > 0 && (
              <div className="mt-1 flex justify-between gap-3">
                <span>Auf dem Gerät verfügbar</span>
                <span className="tabular-nums">{formatBytes(quota.quota - quota.usage)}</span>
              </div>
            )}
          </div>

          {used > 0 && (
            <div className="border-t border-line px-4 py-3">
              <Button variant="danger" onClick={() => void removeAll()} full>
                Heruntergeladene Karten entfernen
              </Button>
            </div>
          )}
        </div>

        {message && (
          <p role="status" className="mt-3 border-l-2 border-accent bg-surface px-3 py-2 text-sm text-ink">
            {message}
          </p>
        )}
      </section>

      <Benachrichtigungen />

      <section className="px-4 pb-5">
        <Label>Erscheinungsbild</Label>
        {/* Eine Leiste statt vier Kacheln: Die vier Ansichten sind eine einzige
            Entscheidung, und nebeneinander liegend ist sie mit einem Blick erfasst.
            Erklärt wird nur die getroffene Wahl — die Hinweise zu den drei anderen
            Möglichkeiten kosten Platz und sagen im Moment des Lesens nichts. */}
        <div
          role="group"
          aria-label="Erscheinungsbild"
          className="mt-3 flex rounded-card border border-line bg-surface"
        >
          {(Object.keys(THEME_LABELS) as Theme[]).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setTheme(option)}
              aria-pressed={theme === option}
              className={
                'tap flex flex-1 items-center justify-center border-l border-line px-0.5 ' +
                'text-xs font-semibold leading-tight first:border-l-0 ' +
                // Die Ecken sitzen an den Segmenten selbst, nicht am Clipping der Hülle:
                // Ein overflow-hidden der Leiste schneidet die gefüllte Fläche in der
                // Ecke nicht zuverlässig ab. Ein Pixel weniger Radius als außen, damit
                // die Füllung innerhalb des Rahmens bleibt statt ihn zu berühren.
                'first:rounded-s-[calc(var(--radius-card)_-_1px)] ' +
                'last:rounded-e-[calc(var(--radius-card)_-_1px)] ' +
                'transition-colors duration-100 ' +
                (theme === option
                  ? 'auf-akzent bg-accent text-accent-ink'
                  : 'text-muted hover:bg-raised')
              }
            >
              {THEME_LABELS[option]}
            </button>
          ))}
        </div>
        <p className="mt-2 text-xs text-muted">{THEME_HINTS[theme]}</p>
      </section>

      <section className="px-4 pb-8">
        <Label>Über die App</Label>
        <div className="mt-3 overflow-hidden rounded-card border border-line bg-surface">
          <div className="space-y-2 px-4 py-3 text-sm text-muted">
            {/* Die Fassung zuerst: Sie ist die Angabe, nach der bei einer Rückfrage
                gefragt wird — „welche Fassung haben Sie?" —, und die einzige Zeile
                hier, hinter der noch etwas zu lesen steht. */}
            <Row term="Fassung" value={APP_VERSION} />
            <Row term="Karten im Katalog" value={String(catalog.cards.length)} />
            <Row term="Katalogstand" value={formatDateTime(catalog.generatedAt)} />
            <Row term="Bezug" value={source === 'lokal' ? 'Lokale Kopie' : 'Aktuell geladen'} />
            <Row term="Kennung" value={catalog.catalogHash.slice(0, 12)} />
          </div>

          <div className="border-t border-line px-4 py-3">
            <Button onClick={() => setVerlaufOffen(true)} full>
              Was ist neu
            </Button>
          </div>
        </div>

        {verlaufOffen && <AenderungenDialog onSchliessen={() => setVerlaufOffen(false)} />}
        <p className="mt-4 text-xs leading-relaxed text-muted">
          Die Karten unterliegen den bei der jeweiligen Karte angegebenen Lizenzen und Quellen.
          Angaben ohne Gewähr — verbindlich sind stets die Vorgaben der eigenen Organisation.
        </p>
      </section>
    </div>
  )
}

/**
 * Benachrichtigungen über neue Karten.
 *
 * Hier steht der Schalter dauerhaft — auch für alle, die die einmalige Frage beim
 * Start verneint haben. Beide Wege lösen die Systemabfrage des Browsers erst auf ein
 * bewusstes Ja hin aus; sie ungefragt vorzusetzen wäre bequemer zu programmieren und
 * in der Wirkung fatal, denn eine weggewischte Abfrage lässt sich nur noch über die
 * Einstellungen des Geräts zurückholen.
 */
function Benachrichtigungen() {
  const [zustand, setZustand] = useState<PushZustand | null>(null)
  const [läuft, setLäuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    void holePushZustand().then(setZustand)
  }, [])

  // Solange der Zustand unbekannt ist, bleibt der Abschnitt leer statt zu flackern.
  if (zustand === null) return null

  const umschalten = async () => {
    setLäuft(true)
    setFehler(null)
    try {
      setZustand(zustand === 'an' ? await schaltePushAus() : await schaltePushEin())
    } catch (problem) {
      setFehler((problem as Error).message)
    } finally {
      setLäuft(false)
    }
  }

  return (
    <section className="px-4 pb-5">
      <Label>Benachrichtigungen</Label>
      <div className="mt-3 rounded-card border border-line bg-surface px-4 py-3">
        {zustand === 'an' || zustand === 'aus' ? (
          <>
            <p className="text-sm text-muted">
              {zustand === 'an'
                ? 'Dieses Gerät wird benachrichtigt, sobald Karten hinzukommen oder überarbeitet werden.'
                : 'Auf Wunsch meldet sich die App, sobald Karten hinzukommen oder überarbeitet werden — auch wenn sie geschlossen ist.'}
            </p>
            <div className="mt-3">
              <Button
                variant={zustand === 'an' ? 'ghost' : 'primary'}
                onClick={() => void umschalten()}
                disabled={läuft}
                full
              >
                {läuft
                  ? 'Einen Moment …'
                  : zustand === 'an'
                    ? 'Benachrichtigungen ausschalten'
                    : 'Benachrichtigungen einschalten'}
              </Button>
            </div>
          </>
        ) : (
          <p className="text-sm text-muted">{HINWEIS[zustand]}</p>
        )}

        {fehler && <p className="mt-2 text-sm text-ink">{fehler}</p>}

        {/* Nur neben dem Schalter: In den Hinweisfällen steht derselbe Satz bereits
            oben, und zweimal dasselbe zu lesen lässt einen Text unaufmerksam wirken. */}
        {(zustand === 'an' || zustand === 'aus') && (
          <p className="mt-3 text-xs leading-relaxed text-muted">
            Unabhängig davon weist die App beim Öffnen auf geänderte Karten hin.
          </p>
        )}
      </div>
    </section>
  )
}

/**
 * Warum der Schalter fehlt.
 *
 * Jeder Fall benennt genau eine Ursache und, wo möglich, den Weg heraus. Im Browser
 * sehen mehrere davon gleich aus — die Push-Schnittstelle ist schlicht nicht da —,
 * haben aber nichts miteinander zu tun: Wer über eine Adresse ohne HTTPS zugreift,
 * kann die App zum Startbildschirm hinzufügen, so oft er will, und es ändert nichts.
 */
const HINWEIS: Record<
  'kein-https' | 'nur-als-app' | 'nicht-unterstuetzt' | 'nicht-eingerichtet' | 'verweigert',
  string
> = {
  'kein-https':
    'Benachrichtigungen brauchen eine gesicherte Verbindung (https). Über eine ' +
    'unverschlüsselte Adresse stellt der Browser sie nicht bereit — beim Testen im ' +
    'eigenen Netz also auch dann nicht, wenn die App zum Startbildschirm hinzugefügt wurde.',
  'nur-als-app':
    'Benachrichtigungen stehen erst zur Verfügung, wenn die App zum Startbildschirm ' +
    'hinzugefügt und von dort geöffnet wird. Auf iPhone und iPad geht das über das ' +
    'Teilen-Symbol und „Zum Home-Bildschirm".',
  'nicht-unterstuetzt':
    'Dieses Gerät unterstützt keine Benachrichtigungen. Die App weist beim Öffnen auf ' +
    'geänderte Karten hin.',
  'nicht-eingerichtet':
    'Auf diesem Server ist kein Benachrichtigungsdienst eingerichtet. Die App weist ' +
    'beim Öffnen auf geänderte Karten hin.',
  verweigert:
    'Benachrichtigungen sind für diese App gesperrt. Das lässt sich nur in den ' +
    'Einstellungen des Geräts wieder ändern.',
}

const THEME_HINTS: Record<Theme, string> = {
  auto: 'Folgt dem Gerät',
  hell: 'Tageslicht, CI-Farben',
  nacht: 'Gedämpft für Dunkelheit',
  rot: 'Erhält die Dunkelanpassung',
}

function Row({ term, value }: { term: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <span>{term}</span>
      <span className="tabular-nums text-ink">{value}</span>
    </div>
  )
}

/** Datenmengen mit deutschem Dezimalkomma und passender Einheit. */
function formatBytes(bytes: number): string {
  const format = (value: number, digits: number) =>
    value.toLocaleString('de-DE', { minimumFractionDigits: digits, maximumFractionDigits: digits })

  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${format(bytes / 1024, 0)} kB`
  if (bytes < 1024 * 1024 * 1024) return `${format(bytes / (1024 * 1024), 1)} MB`
  return `${format(bytes / (1024 * 1024 * 1024), 1)} GB`
}

function formatDateTime(iso: string): string {
  const date = new Date(iso)
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}
